import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import type { QuizQuestion } from '../../../domain';
import { containFitScale } from '../../../infrastructure/runtime';
import '../components/atomic/layout/app_screen/app_screen.css';
import { SunBeams } from '../components/atomic/atmosphere/sun_beams/SunBeams';
import { FrameHeader } from '../components/atomic/dialog/frame_header/FrameHeader';
import { SolidFrameBackground } from '../components/atomic/dialog/solid_frame_background/SolidFrameBackground';
import { SkyAtmosphere } from '../components/composite/atmosphere/sky_atmosphere/SkyAtmosphere';
import { useAppFit } from '../../hooks/useAppFit';
import { QuizAnswerButton } from './answer_button/QuizAnswerButton';
import { QuizImage } from './content/QuizImage';
import { QuizDailyLimitModal } from './daily_limit/QuizDailyLimitModal';
import { GoBackButton } from './go_back_button/GoBackButton';
import { Header } from './header/Header';
import type { QuizScreenController } from './QuizScreenController';
import {
  getQuizAnswerButtonState,
  type QuizAnswerFeedback,
  type QuizAnswerSlot,
  type QuizScreenModel,
} from './QuizScreenModelScreen';
import { ClueLink } from './texts/ClueLink';
import { QuestionText } from './texts/QuestionText';
import { QuizTicketPopup } from './ticket_popup/QuizTicketPopup';
import './quiz_screen.css';
import type { ScreenViewProps } from '../shared/screenViewProps';

export type QuizScreenViewProps = ScreenViewProps<
  QuizScreenModel,
  QuizScreenController
>;

const QUIZ_COMPLETE_TEXT = 'Все вопросы пройдены!';
const QUESTION_AFTER_IMAGE_MS = 280;
const ANSWERS_AFTER_QUESTION_MS = 280;

type QuizContentStage = 'image' | 'question' | 'answers';

function useQuizContentReveal() {
  const [imageReady, setImageReady] = useState(false);
  const [stage, setStage] = useState<QuizContentStage>('image');

  const handleImageReady = useCallback(() => {
    setImageReady(true);
  }, []);

  useEffect(() => {
    if (!imageReady) {
      return;
    }

    const questionTimer = window.setTimeout(() => {
      setStage('question');
    }, QUESTION_AFTER_IMAGE_MS);
    const answersTimer = window.setTimeout(() => {
      setStage('answers');
    }, QUESTION_AFTER_IMAGE_MS + ANSWERS_AFTER_QUESTION_MS);

    return () => {
      window.clearTimeout(questionTimer);
      window.clearTimeout(answersTimer);
    };
  }, [imageReady]);

  return {
    imageVisible: imageReady,
    questionVisible: imageReady && (stage === 'question' || stage === 'answers'),
    answersVisible: imageReady && stage === 'answers',
    handleImageReady,
  };
}

type QuizQuestionContentProps = {
  question: QuizQuestion | null;
  feedback: QuizAnswerFeedback | null;
  answerSlots: readonly QuizAnswerSlot[];
  controller: QuizScreenController;
};

function QuizQuestionContent({
  question,
  feedback,
  answerSlots,
  controller,
}: QuizQuestionContentProps) {
  const answersLocked = feedback != null;
  const { imageVisible, questionVisible, answersVisible, handleImageReady } =
    useQuizContentReveal();

  return (
    <>
      <QuizImage
        questionId={question?.id}
        ready={imageVisible}
        onReady={handleImageReady}
      />
      {questionVisible ? (
        <QuestionText text={question?.prompt ?? QUIZ_COMPLETE_TEXT} />
      ) : null}
      {answersVisible
        ? question?.options.map((option, index) => {
            const slot = answerSlots[index];
            if (slot == null) {
              return null;
            }

            return (
              <QuizAnswerButton
                key={`${question.id}-${index}`}
                top={slot.top}
                label={option}
                state={getQuizAnswerButtonState(index, feedback)}
                disabled={answersLocked}
                onClick={() => controller.handleAnswer(index)}
              />
            );
          })
        : null}
      {answersVisible && question?.rutubeUrl ? (
        <ClueLink href={question.rutubeUrl} onClick={controller.handleClueClick} />
      ) : null}
    </>
  );
}

export function QuizScreenView({ model, controller }: QuizScreenViewProps) {
  const { question, feedback, showTicketReward, showDailyLimit } = model;
  const fit = useAppFit();
  const contain = containFitScale(fit.scale);

  return (
    <div className="app-screen quiz-screen">
      <SkyAtmosphere variant="welcome" />
      <div
        className="quiz-plaques"
        style={
          {
            '--fit-contain-inv-x': String(contain.invX),
            '--fit-contain-inv-y': String(contain.invY),
            '--fit-contain-uniform': String(contain.uniform),
          } as CSSProperties
        }
      >
        <SunBeams className="quiz-screen__sun-beams" />
        <SolidFrameBackground />
        <FrameHeader showSubHeader={false} />
        {showDailyLimit ? null : <Header />}
        {showDailyLimit ? null : (
          <QuizQuestionContent
            key={question?.id ?? 'complete'}
            question={question}
            feedback={feedback}
            answerSlots={model.answerSlots}
            controller={controller}
          />
        )}
        {showTicketReward ? <QuizTicketPopup /> : null}
        <GoBackButton onClick={controller.handleGoBack} />
        {showDailyLimit ? (
          <QuizDailyLimitModal onZero={controller.handleDailyLimitZero} />
        ) : null}
      </div>
    </div>
  );
}
